import { Feature } from "@enums";
import { Add } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Divider,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import BottomActionBar from "@pages/Layout/BottomActionBar";
import {
  ErrorBox,
  StatusPageDialog,
  WarningBox,
} from "@pages/OrderTracker/components/PlanningAutomation/StatusPageComponents";
import { StatusPageMockComponent } from "@pages/OrderTracker/components/PlanningAutomation/StatusPageMockComponent";
import {
  AutomationStatus,
  getAutomationStatus,
  getElapsedTimeSubtext,
  getPlanProgressPercentage,
  getStatusColour,
} from "@pages/OrderTracker/components/PlanningAutomation/utils";
import {
  PlanningProgress,
  PlanningStepSequence,
  useAutomationIntegrationApiV1GetPlanGenerationQuery,
  useAutomationIntegrationApiV1GetPlanningProgressQuery,
  useAutomationIntegrationApiV1GetPlanningStepsQuery,
  useWebApiV1GetUserFeatureSwitchesQuery,
  useWebApiV1OrderQuery,
} from "@providers/hop-ord-server/api";
import { CompletedPlans, Timer } from "@volatile/pages/OrderTracker/components";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

interface Props {
  orderId: number;
}

const AutomatedPlanStatusPage = ({ orderId }: Props): JSX.Element => {
  const navigate = useNavigate();
  const theme = useTheme();

  const { data: order } = useWebApiV1OrderQuery({ orderId });

  // fetch steps unless they've already been fetched
  const planningStepsRef = useRef<PlanningStepSequence | undefined>(undefined);

  const shouldPollSteps = !planningStepsRef.current?.steps.length;
  const { data: planningSteps } =
    useAutomationIntegrationApiV1GetPlanningStepsQuery(
      { orderId },
      // poll until steps are available
      { pollingInterval: shouldPollSteps ? 5000 : 0, skip: !shouldPollSteps },
    );

  useEffect(() => {
    planningStepsRef.current = planningSteps;
  }, [planningSteps]);

  const { data: completedPlans } =
    useAutomationIntegrationApiV1GetPlanGenerationQuery({
      orderId: orderId,
    });

  const { data: featureSwitchData } =
    useWebApiV1GetUserFeatureSwitchesQuery(undefined);

  const showMockControls = featureSwitchData?.find(
    (feature) => feature.name === Feature.MOCK_CONTROLS,
  );
  const showPlanReview = featureSwitchData?.find(
    (feature) => feature.name === Feature.PLAN_REVIEW,
  );

  // fetch progress until progress contains the generation result
  const planningProgressRef = useRef<PlanningProgress | undefined>(undefined);

  const shouldPollProgress =
    planningStepsRef.current?.steps.length &&
    !planningProgressRef.current?.result;

  const { data: planningProgressData } =
    useAutomationIntegrationApiV1GetPlanningProgressQuery(
      { orderId },
      {
        pollingInterval: shouldPollProgress ? 5000 : 0,
        // skip there are no steps or there is already a planning result
        skip: !shouldPollProgress,
      },
    );

  useEffect(() => {
    planningProgressRef.current = planningProgressData;
  }, [planningProgressData]);

  const [showStatusDialog, setShowStatusDialog] = useState(false);

  if (!order) {
    return <></>;
  }

  const planningProgress =
    planningProgressData || order.latestPlanGenerationAttempt;

  const startTime = dayjs(planningProgress?.planningStartedAt);

  const completedTime = planningProgress?.result?.finishedAt
    ? dayjs(planningProgress.result.finishedAt)
    : null;

  const automationStatus = planningProgress
    ? getAutomationStatus(planningProgress)
    : order?.latestPlanGenerationAttempt?.status;

  const elapsedTimeSubtext = getElapsedTimeSubtext(
    automationStatus as AutomationStatus,
  );

  const planProgressPercentage =
    planningSteps && planningProgress
      ? getPlanProgressPercentage(
          planningSteps.steps,
          planningProgress.currentStep,
        )
      : order?.latestPlanGenerationAttempt?.planProgressPercentage || 0;

  const planningAutomationCompleted =
    automationStatus === "success" || automationStatus === "error";

  const currentStepIndex =
    planningSteps?.steps.indexOf(planningProgress?.currentStep || "") ?? -1;

  return (
    <Stack gap={4}>
      <Card>
        <Box flex={1}>
          <Stack padding={2} width={1}>
            <Stack direction="row" justifyContent={"space-between"}>
              <Typography variant="subtitle1">
                Planning automation
                {planningProgress?.templateName
                  ? `: ${planningProgress?.templateName}`
                  : ""}
              </Typography>
              <Typography variant="body2">Machine {order.machine}</Typography>
            </Stack>
            <Stack direction="row" gap={0.5}>
              <Typography variant="body2" color="text.secondary">
                {elapsedTimeSubtext}
              </Typography>
              {automationStatus !== "not_started" && (
                <Timer
                  startTime={startTime}
                  completedTime={completedTime}
                  currentStep={planningProgress?.currentStep}
                  stepSequence={planningSteps?.steps}
                  orderId={orderId}
                />
              )}
            </Stack>
            <LinearProgress
              sx={{ marginBottom: 2, marginTop: 3 }}
              variant="determinate"
              value={planProgressPercentage}
              color={getStatusColour(automationStatus as AutomationStatus)}
            />
            {automationStatus !== "not_started" && (
              <Typography variant="body2" color="text.secondary">
                {currentStepIndex + 1}/{planningSteps?.steps.length}{" "}
                {planningProgress?.currentStep}
              </Typography>
            )}
            {automationStatus === "error" && (
              <ErrorBox
                error={planningProgress?.result?.error || ""}
                errorDate={planningProgress?.result?.finishedAt || ""}
                numberOfWarnings={planningProgress?.warnings?.length || 0}
                onClick={() => setShowStatusDialog(true)}
              />
            )}
            {automationStatus !== "error" &&
            planningProgress?.warnings.length ? (
              <WarningBox
                onClick={() => setShowStatusDialog(true)}
                numberOfWarnings={planningProgress.warnings.length}
              />
            ) : null}
            <Divider sx={{ marginTop: 1, marginX: -2 }} />
            <Button
              variant="outlined"
              onClick={() => {
                navigate(`/commandcenter/${order.id}`);
              }}
              disabled={!planningAutomationCompleted}
              sx={{ width: "max-content", paddingX: 2, marginTop: 2 }}
              startIcon={<Add />}
            >
              Generate another plan
            </Button>
          </Stack>
        </Box>
      </Card>
      {showMockControls && (
        <StatusPageMockComponent
          orderId={order.id}
          eventDetails={planningSteps?.steps as string[]}
        />
      )}

      {(planningAutomationCompleted || (completedPlans || []).length > 0) && (
        <>
          <Divider sx={{ width: 1 }} />
          <CompletedPlans plans={completedPlans || []} />
        </>
      )}

      <StatusPageDialog
        warnings={planningProgress?.warnings ?? []}
        finishedAt={planningProgress?.result?.finishedAt}
        open={showStatusDialog}
        onClose={() => setShowStatusDialog(false)}
      />
      <BottomActionBar>
        <Stack
          direction="row"
          width={1}
          justifyContent="flex-end"
          padding={2}
          borderTop={`1px solid ${theme.palette.divider}`}
        >
          <Button
            variant="contained"
            color="primary"
            disabled={!planningAutomationCompleted}
            onClick={() => {
              if (showPlanReview) {
                navigate(`/commandcenter/${order.id}/plan-review`);
              } else {
                navigate(`/commandcenter/${order.id}/handover`);
              }
            }}
            sx={{
              opacity: !planningAutomationCompleted ? 0.6 : 1,
              cursor: !planningAutomationCompleted ? "not-allowed" : "pointer",
            }}
          >
            Next: {showPlanReview ? "Plan Review" : "Handover"}
          </Button>
        </Stack>
      </BottomActionBar>
    </Stack>
  );
};

export default AutomatedPlanStatusPage;
